import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { FiClipboard, FiGift, FiPhone, FiMail, FiMapPin, FiCheckCircle } from 'react-icons/fi';
import { BiHeadphone } from 'react-icons/bi';
import { IoLogoWhatsapp } from 'react-icons/io5';
import './Booking.css';

const Booking = () => {
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    eventType: '',
    preferredDate: '',
    alternateDate: '',
    guestCount: '',
    duration: '',
    hallPreference: '',
    specialReq: '',
    source: ''
  });

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setIsSubmitted(true);
    // In a real application, we would handle the form submission to an API here
  };

  const fadeUpVariant = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6 } }
  };

  return (
    <div className="booking-page">
      {/* 1. Page Hero */}
      <section className="booking-hero">
        <div className="booking-hero-content">
          <motion.h1
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            Book Your Celebration
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            Let Us Create Your Perfect Event
          </motion.p>
        </div>
      </section>

      {/* 2. Booking Benefits */}
      <section className="booking-benefits">
        <BenefitCard 
          icon={<FiClipboard />} 
          title="Personalized Planning" 
          desc="Our expert coordinators tailor every detail to your vision, ensuring a flawless execution of your event."
          delay={0}
        />
        <BenefitCard 
          icon={<FiGift />} 
          title="Flexible Packages" 
          desc="Choose from a variety of premium packages designed to suit different event types, sizes, and preferences."
          delay={0.2}
        />
        <BenefitCard 
          icon={<BiHeadphone />} 
          title="Dedicated Support" 
          desc="Enjoy peace of mind with our dedicated support team, available to assist you at every step of your journey."
          delay={0.4}
        />
      </section>

      {/* 3. Premium Enquiry Form */}
      <section className="booking-form-section">
        <div className="form-header">
          <motion.h2
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={fadeUpVariant}
          >
            Event Enquiry
          </motion.h2>
          <motion.p
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={fadeUpVariant}
          >
            Please provide us with details about your upcoming event, and we will get back to you with a customized proposal.
          </motion.p>
        </div>

        <div className="booking-form-container">
          {isSubmitted ? (
            <motion.div 
              className="success-message"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
            >
              <FiCheckCircle className="success-icon" />
              <h3>Thank You for Your Enquiry</h3>
              <p>Your request has been received successfully. Our event coordinators will review your details and contact you within 24 hours.</p>
            </motion.div>
          ) : (
            <form className="enquiry-form" onSubmit={handleSubmit}>
              <div className="form-group">
                <label>Full Name</label>
                <input type="text" name="fullName" value={formData.fullName} onChange={handleChange} required placeholder="John Doe" />
              </div>
              <div className="form-group">
                <label>Email Address</label>
                <input type="email" name="email" value={formData.email} onChange={handleChange} required placeholder="john@example.com" />
              </div>
              <div className="form-group">
                <label>Phone Number</label>
                <input type="tel" name="phone" value={formData.phone} onChange={handleChange} required placeholder="+91 94470 25460" />
              </div>
              <div className="form-group">
                <label>Event Type</label>
                <select name="eventType" value={formData.eventType} onChange={handleChange} required>
                  <option value="">Select Event Type</option>
                  <option value="Wedding Ceremony">Wedding Ceremony</option>
                  <option value="Wedding Reception">Wedding Reception</option>
                  <option value="Engagement">Engagement</option>
                  <option value="Corporate Event">Corporate Event</option>
                  <option value="Birthday Party">Birthday Party</option>
                  <option value="Family Gathering">Family Gathering</option>
                  <option value="Award Ceremony">Award Ceremony</option>
                  <option value="Cultural Event">Cultural Event</option>
                  <option value="Exhibition">Exhibition</option>
                  <option value="Seminar">Seminar</option>
                  <option value="Other">Other</option>
                </select>
              </div>
              <div className="form-group">
                <label>Preferred Date</label>
                <input type="date" name="preferredDate" value={formData.preferredDate} onChange={handleChange} required />
              </div>
              <div className="form-group">
                <label>Alternate Date</label>
                <input type="date" name="alternateDate" value={formData.alternateDate} onChange={handleChange} />
              </div>
              <div className="form-group">
                <label>Expected Guest Count</label>
                <input type="number" name="guestCount" value={formData.guestCount} onChange={handleChange} required min="1" placeholder="e.g. 500" />
              </div>
              <div className="form-group">
                <label>Event Duration</label>
                <select name="duration" value={formData.duration} onChange={handleChange} required>
                  <option value="">Select Duration</option>
                  <option value="Half Day">Half Day</option>
                  <option value="Full Day">Full Day</option>
                  <option value="Multi-Day">Multi-Day</option>
                </select>
              </div>
              <div className="form-group full-width">
                <label>Hall Preference</label>
                <select name="hallPreference" value={formData.hallPreference} onChange={handleChange} required>
                  <option value="">Select Hall</option>
                  <option value="Grand Hall">Grand Hall</option>
                  <option value="Dining Hall">Dining Hall</option>
                  <option value="Both">Both (Grand Hall & Dining Hall)</option>
                  <option value="Not Sure">Not Sure Yet</option>
                </select>
              </div>
              <div className="form-group full-width">
                <label>Special Requirements</label>
                <textarea name="specialReq" value={formData.specialReq} onChange={handleChange} placeholder="Please let us know about any specific themes, catering needs, or accessibility requirements..."></textarea>
              </div>
              <div className="form-group full-width">
                <label>How did you hear about us?</label>
                <select name="source" value={formData.source} onChange={handleChange}>
                  <option value="">Please Select</option>
                  <option value="Google">Google Search</option>
                  <option value="Social Media">Social Media</option>
                  <option value="Friend/Family">Friend or Family</option>
                  <option value="Wedding Planner">Wedding Planner</option>
                  <option value="Other">Other</option>
                </select>
              </div>
              <button type="submit" className="submit-btn">Submit Enquiry</button>
            </form>
          )}
        </div>
      </section>

      {/* 4. Quick Contact Section */}
      <section className="quick-contact">
        <ContactCard 
          icon={<IoLogoWhatsapp className="wa-icon-large" />} 
          title="WhatsApp Quick Enquiry" 
          content={<a href="https://wa.me/919447025460" target="_blank" rel="noreferrer" className="whatsapp-btn"><IoLogoWhatsapp className="wa-icon"/> Chat on WhatsApp</a>}
          delay={0}
        />
        <ContactCard 
          icon={<FiPhone />} 
          title="Call Us" 
          content={<a href="tel:+919447025460">+91 94470 25460</a>}
          delay={0.1}
        />
        <ContactCard 
          icon={<FiMail />} 
          title="Email Us" 
          content={<a href="mailto:info@Prasannalakshmi.com">info@Prasannalakshmi.com</a>}
          delay={0.2}
        />
        <ContactCard 
          icon={<FiMapPin />} 
          title="Visit Us" 
          content={<p>Kanjikode, Palakkad<br/>Kerala, India</p>}
          delay={0.3}
        />
      </section>

      {/* 5. Availability CTA */}
      <section className="availability-cta">
        <motion.div 
          className="availability-content"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={fadeUpVariant}
        >
          <h2>Check Availability for Your Date</h2>
          <div className="availability-checker">
            <input type="date" aria-label="Check Date" />
            <button type="button">Check Date</button>
          </div>
        </motion.div>
      </section>

      {/* 6. Google Maps Embed */}
      <section className="map-section">
        <iframe 
          src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d31298.03!2d76.5!3d10.78!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3ba7cf1bb3bb79e3%3A0x2e3e0e3b0e3b0e3b!2sKanjikode%2C%20Palakkad!5e0!3m2!1sen!2sin!4v1700000000000" 
          allowFullScreen="" 
          loading="lazy" 
          referrerPolicy="no-referrer-when-downgrade"
          title="Prasannalakshmi Kalyana Mandapam & Auditorium Kalyana Mandapam & Auditorium Location"
        ></iframe>
      </section>

      {/* 7. Final Note */}
      <section className="final-note">
        <p>Our event coordinators will respond within 24 hours to help plan your perfect celebration.</p>
      </section>
    </div>
  );
};

const BenefitCard = ({ icon, title, desc, delay }) => {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.2 });
  return (
    <motion.div 
      ref={ref}
      className="benefit-card"
      initial={{ opacity: 0, y: 40 }}
      animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 40 }}
      transition={{ duration: 0.6, delay }}
    >
      <div className="benefit-icon">{icon}</div>
      <h3>{title}</h3>
      <p>{desc}</p>
    </motion.div>
  );
};

const ContactCard = ({ icon, title, content, delay }) => {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.2 });
  return (
    <motion.div 
      ref={ref}
      className="contact-card"
      initial={{ opacity: 0, y: 30 }}
      animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
      transition={{ duration: 0.5, delay }}
    >
      <div className="contact-icon">{icon}</div>
      <h4>{title}</h4>
      {content}
    </motion.div>
  );
};

export default Booking;
